import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const GUARD = "__unsavedGuard";

// Blocks every way of leaving the page while `active`: links, browser back/forward, reload/close.
export function useUnsavedChangesGuard(active, onDiscard) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // { type: "link", to } | { type: "back" } | { type: "fn", fn }
  const bypass = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    window.__rallyConfirmLeave = (fn) => { setPending({ type: "fn", fn }); return true; };
    return () => { delete window.__rallyConfirmLeave; };
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    bypass.current = false;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onClick = (e) => {
      if (bypass.current || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.("a[href]");
      if (!a || a.target === "_blank" || a.hasAttribute("download")) return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      if (url.pathname + url.search === window.location.pathname + window.location.search) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ type: "link", to: url.pathname + url.search + url.hash });
    };
    const arm = () => window.history.pushState({ ...(window.history.state || {}), [GUARD]: true }, "", window.location.href);
    const onPop = (e) => {
      if (bypass.current) return;
      e.stopImmediatePropagation();
      arm();
      setPending({ type: "back" });
    };
    if (!window.history.state?.[GUARD]) arm();
    window.addEventListener("beforeunload", onUnload);
    document.addEventListener("click", onClick, true);
    window.addEventListener("popstate", onPop, true);
    return () => {
      window.removeEventListener("beforeunload", onUnload);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("popstate", onPop, true);
      if (!bypass.current && window.history.state?.[GUARD]) window.history.back();
    };
  }, [active]);

  const stay = () => setPending(null);
  const discard = () => {
    const p = pending;
    bypass.current = true;
    setPending(null);
    onDiscard && onDiscard();
    if (p?.type === "link") navigate(p.to, { replace: !!window.history.state?.[GUARD] });
    else if (p?.type === "fn") p.fn();
    else window.history.go(-2);
  };
  return { open: !!pending, stay, discard };
}
